import { Injectable } from '@angular/core';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { catchError, map, Observable, of } from 'rxjs';
import { ApiService } from '@core/services/api.service';
import { API_ENDPOINTS } from '@core/constants/api-endpoints.constants';
import { OtpDetails, OtpPayload, OtpResponse, PageResponse, Result, SuccessResponse } from '@core/models/common.model';
import {
  Organization,
  OrganizationDetailsResponse,
  OrgListFilterCriteria,
  OrgPayload,
} from '@core/models/org.model';
import {
  PaymentCapturePayload,
  SubscriptionActivationPayload,
  SubscriptionDetails,
  SubscriptionPayload,
} from '@core/models/subscription.model';
import { OrderSummaryRequest, OrderSummaryResponse } from '@core/models/order-summary.model';
import { UserPayload, UserProfile, UserProfileWithOTP } from '@core/models/user.model';
import { coerceApiResult, mapHttpErrorToResult } from '@shared/utils/api-response.utils';

@Injectable({
  providedIn: 'root',
})
export class EnterpriseService {
  constructor(private readonly apiService: ApiService) {}

  getIncompleteEnterprises(
    params: Record<string, string | number>,
  ): Observable<Result<PageResponse<UserProfile>>> {
    return this.apiService
      .get<Result<PageResponse<UserProfile>>>(API_ENDPOINTS.ADMIN.INCOMPLETE_LIST, { params })
      .pipe(
        map((response) => coerceApiResult<PageResponse<UserProfile>>(response, 'LOAD_INCOMPLETE_ENTERPRISES')),
        catchError((error) =>
          of(mapHttpErrorToResult<PageResponse<UserProfile>>(error, 'LOAD_INCOMPLETE_ENTERPRISES')),
        ),
      );
  }

  getOrganizationList(
    params: OrgListFilterCriteria,
  ): Observable<Result<PageResponse<OrganizationDetailsResponse>>> {
    return this.apiService
      .get<Result<PageResponse<OrganizationDetailsResponse>>>(API_ENDPOINTS.ORGANIZATION.BASE, {
        params: params as Record<string, string | number>,
      })
      .pipe(
        map((response) =>
          coerceApiResult<PageResponse<OrganizationDetailsResponse>>(response, 'LOAD_ENTERPRISES'),
        ),
        catchError((error) =>
          of(mapHttpErrorToResult<PageResponse<OrganizationDetailsResponse>>(error, 'LOAD_ENTERPRISES')),
        ),
      );
  }

  getOrganizationById(orgId: string): Observable<Result<OrganizationDetailsResponse>> {
    return this.apiService
      .get<Result<OrganizationDetailsResponse>>(`${API_ENDPOINTS.ORGANIZATION.BASE}/${orgId}`)
      .pipe(
        map((response) => coerceApiResult<OrganizationDetailsResponse>(response, 'LOAD_ENTERPRISE')),
        catchError((error) =>
          of(mapHttpErrorToResult<OrganizationDetailsResponse>(error, 'LOAD_ENTERPRISE')),
        ),
      );
  }

  registerAdminUser(payload: UserPayload): Observable<Result<UserProfileWithOTP>> {
    const userPayload: UserPayload = {
      ...payload,
      email: payload.email?.trim(),
      firstName: payload.firstName?.trim(),
      lastName: payload.lastName?.trim(),
      mobileNumber: payload.mobileNumber?.trim(),
      profileIdentifiers: [
        {
          emailId: payload.email,
          uid: '',
          providerId: '',
          registrationMethod: payload.profileIdentifiers?.[0]?.registrationMethod || '',
        },
      ],
      termsConditionsDetails: {
        tandcLink: 'dummy',
        tCAcceptedStatus: payload.termsConditionsDetails?.tCAcceptedStatus ?? true,
      },
    };

    return this.apiService
      .post<Result<UserProfileWithOTP>, UserPayload>(API_ENDPOINTS.USERS.ADMIN, userPayload)
      .pipe(
        map((response) => coerceApiResult<UserProfileWithOTP>(response, 'CREATE_ENTERPRISE_USER')),
        catchError((error) =>
          of(mapHttpErrorToResult<UserProfileWithOTP>(error, 'CREATE_ENTERPRISE_USER')),
        ),
      );
  }

  verifyOtp(payload: OtpPayload): Observable<Result<unknown>> {
    return this.apiService.post<OtpResponse, OtpPayload>(API_ENDPOINTS.AUTH.VERIFY_OTP, payload).pipe(
      map((response) =>
        response?.success
          ? { success: true as const, data: response.data }
          : coerceApiResult<unknown>(response, 'VERIFY_OTP'),
      ),
      catchError((error) => of(mapHttpErrorToResult<unknown>(error, 'VERIFY_OTP'))),
    );
  }

  sendSignupOtp(emailId: string): Observable<Result<OtpDetails>> {
    return this.apiService
      .post<SuccessResponse<OtpDetails> | OtpResponse, { emailId: string; event: string }>(
        API_ENDPOINTS.AUTH.SEND_OTP,
        { emailId, event: 'SIGNUP' },
      )
      .pipe(
        map((response) => {
          if (response && typeof response === 'object' && 'success' in response && response.success) {
            const data = 'data' in response ? (response.data as OtpDetails | undefined) : undefined;
            return data
              ? { success: true as const, data: { ...data, emailId } }
              : coerceApiResult<OtpDetails>(response, 'VERIFY_OTP');
          }

          return coerceApiResult<OtpDetails>(response, 'VERIFY_OTP');
        }),
        catchError((error) => of(mapHttpErrorToResult<OtpDetails>(error, 'VERIFY_OTP'))),
      );
  }

  resendOtp(payload: Pick<OtpPayload, 'event' | 'referenceId' | 'otpAlpha'>): Observable<Result<OtpDetails>> {
    return this.apiService
      .post<SuccessResponse<OtpDetails> | OtpResponse, OtpPayload>(
        API_ENDPOINTS.AUTH.RESEND_OTP,
        { ...payload, otp: '' },
      )
      .pipe(
        map((response) => {
          if (response && typeof response === 'object' && 'success' in response && response.success) {
            const data = 'data' in response ? (response.data as OtpDetails | undefined) : undefined;
            return data
              ? { success: true as const, data }
              : coerceApiResult<OtpDetails>(response, 'VERIFY_OTP');
          }

          return coerceApiResult<OtpDetails>(response, 'VERIFY_OTP');
        }),
        catchError((error) => of(mapHttpErrorToResult<OtpDetails>(error, 'VERIFY_OTP'))),
      );
  }

  createOrganization(payload: OrgPayload, uid: string): Observable<Result<Organization>> {
    const params = new HttpParams().set('uid', uid);

    return this.apiService
      .post<Result<Organization>, OrgPayload>(API_ENDPOINTS.ORGANIZATION.ASSISTED, payload, { params })
      .pipe(
        map((response) => coerceApiResult<Organization>(response, 'CREATE_ORGANIZATION')),
        catchError((error) => of(mapHttpErrorToResult<Organization>(error, 'CREATE_ORGANIZATION'))),
      );
  }

  updateOrganization(orgId: string, organization: Organization): Observable<Result<Organization>> {
    return this.apiService
      .put<Result<Organization>>(API_ENDPOINTS.ORGANIZATION.UPDATE(orgId), organization)
      .pipe(
        map((response) => coerceApiResult<Organization>(response, 'UPDATE_ORGANIZATION')),
        catchError((error) => of(mapHttpErrorToResult<Organization>(error, 'UPDATE_ORGANIZATION'))),
      );
  }

  uploadOrganizationLogo(
    orgId: string,
    file: File,
  ): Observable<Result<OrganizationDetailsResponse>> {
    const formData = new FormData();
    formData.append('orgLogo', file);

    return this.apiService
      .put<Result<OrganizationDetailsResponse>>(API_ENDPOINTS.ORGANIZATION.LOGO(orgId), formData, {
        headers: new HttpHeaders({ Accept: 'application/json' }),
      })
      .pipe(
        map((response) => coerceApiResult<OrganizationDetailsResponse>(response, 'UPDATE_ORGANIZATION')),
        catchError((error) =>
          of(mapHttpErrorToResult<OrganizationDetailsResponse>(error, 'UPDATE_ORGANIZATION')),
        ),
      );
  }

  createSubscription(
    payload: SubscriptionPayload,
    uid: string,
  ): Observable<Result<SubscriptionDetails>> {
    const params = new HttpParams().set('uid', uid);

    return this.apiService
      .post<Result<SubscriptionDetails>, SubscriptionPayload>(API_ENDPOINTS.SUBSCRIPTION.CREATE, payload, {
        params,
      })
      .pipe(
        map((response) => coerceApiResult<SubscriptionDetails>(response, 'CREATE_SUBSCRIPTION')),
        catchError((error) =>
          of(mapHttpErrorToResult<SubscriptionDetails>(error, 'CREATE_SUBSCRIPTION')),
        ),
      );
  }

  getOrderSummary(payload: OrderSummaryRequest, uid: string): Observable<Result<OrderSummaryResponse>> {
    const params = new HttpParams().set('uid', uid);

    return this.apiService
      .post<Result<OrderSummaryResponse>, OrderSummaryRequest>(
        API_ENDPOINTS.SUBSCRIPTION.ADMIN_ORDER_SUMMARY,
        payload,
        { params },
      )
      .pipe(
        map((response) => coerceApiResult<OrderSummaryResponse>(response, 'LOAD_ORDER_SUMMARY')),
        catchError((error) =>
          of(mapHttpErrorToResult<OrderSummaryResponse>(error, 'LOAD_ORDER_SUMMARY')),
        ),
      );
  }

  capturePayment(
    payload: PaymentCapturePayload,
    customerId: string,
    uid: string,
  ): Observable<Result<SubscriptionDetails>> {
    const params = new HttpParams().set('uid', uid);

    return this.apiService
      .put<Result<SubscriptionDetails>>(API_ENDPOINTS.SUBSCRIPTION.PAYMENT_CAPTURE(customerId), payload, {
        params,
      })
      .pipe(
        map((response) => coerceApiResult<SubscriptionDetails>(response, 'CAPTURE_PAYMENT')),
        catchError((error) =>
          of(mapHttpErrorToResult<SubscriptionDetails>(error, 'CAPTURE_PAYMENT')),
        ),
      );
  }

  getActiveSubscription(customerId: string): Observable<Result<SubscriptionDetails>> {
    return this.apiService
      .get<Result<SubscriptionDetails>>(API_ENDPOINTS.SUBSCRIPTION.GET_ACTIVE(customerId))
      .pipe(
        map((response) => coerceApiResult<SubscriptionDetails>(response, 'CREATE_SUBSCRIPTION')),
        catchError((error) =>
          of(mapHttpErrorToResult<SubscriptionDetails>(error, 'CREATE_SUBSCRIPTION')),
        ),
      );
  }

  activateSubscription(
    payload: SubscriptionActivationPayload,
    customerId: string,
    uid: string,
  ): Observable<Result<SubscriptionDetails>> {
    const params = new HttpParams().set('uid', uid);

    return this.apiService
      .patch<Result<SubscriptionDetails>>(API_ENDPOINTS.SUBSCRIPTION.ACTIVATE(customerId), payload, {
        params,
      })
      .pipe(
        map((response) => coerceApiResult<SubscriptionDetails>(response, 'ACTIVATE_SUBSCRIPTION')),
        catchError((error) =>
          of(mapHttpErrorToResult<SubscriptionDetails>(error, 'ACTIVATE_SUBSCRIPTION')),
        ),
      );
  }

  getPlans(): Observable<Result<{ content: import('@core/models/plan.model').Plan[] }>> {
    return this.apiService
      .get<Result<{ content: import('@core/models/plan.model').Plan[] }>>(API_ENDPOINTS.PLANS.BASE)
      .pipe(
        map((response) =>
          coerceApiResult<{ content: import('@core/models/plan.model').Plan[] }>(response, 'LOAD_PLANS'),
        ),
        catchError((error) =>
          of(mapHttpErrorToResult<{ content: import('@core/models/plan.model').Plan[] }>(error, 'LOAD_PLANS')),
        ),
      );
  }
}
